<template>
  <TransitionRoot as="template" :show="isShowing">
    <Dialog as="div" class="relative z-40" @close="onClose">
      <TransitionChild
        as="template"
        enter="ds-sidebar-overlay-enter"
        enter-from="ds-sidebar-overlay-enter-from"
        enter-to="ds-sidebar-overlay-enter-to"
        leave="ds-sidebar-overlay-leave"
        leave-from="ds-sidebar-overlay-leave-from"
        leave-to="ds-sidebar-overlay-leave-to"
      >
        <div
          class="ds-sidebar-overlay"
          data-testid="ds-sidebar-overlay"
          @click="onClose"
        />
      </TransitionChild>
      <div class="ds-sidebar" data-testid="ds-sidebar">
        <div class="ds-sidebar-wrapper">
          <div
            :class="[
              'ds-sidebar-wrapper-container',
              `ds-sidebar-${side}`,
              widthCssClass,
            ]"
            data-testid="ds-sidebar-wrapper-container"
          >
            <TransitionChild
              as="template"
              enter="ds-sidebar-enter"
              :enter-from="transition"
              enter-to="ds-sidebar-enter-to"
              leave="ds-sidebar-leave"
              leave-from="ds-sidebar-leave-from"
              :leave-to="transition"
            >
              <DialogPanel
                class="ds-sidebar-container"
                data-testid="ds-sidebar-container"
              >
                <TransitionChild
                  as="template"
                  enter="ds-sidebar-container-enter"
                  enter-from="ds-sidebar-container-enter-from"
                  enter-to="ds-sidebar-container-enter-to"
                  leave="ds-sidebar-container-leave"
                  leave-from="ds-sidebar-container-leave-from"
                  leave-to="ds-sidebar-container-leave-to"
                >
                  <div
                    :class="`ds-sidebar-container-close ds-sidebar-container-${side}`"
                    data-testid="ds-sidebar-container-close"
                  >
                    <button
                      type="button"
                      class="ds-sidebar-container-close-button"
                      data-testid="ds-sidebar-container-close-button"
                      @click="onClose"
                    >
                      <DsCloseOutlineIcon
                        class="ds-sidebar-container-close-icon"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </TransitionChild>
                <div
                  :class="['ds-sidebar-content', overflowCssClass]"
                  data-testid="ds-sidebar-content"
                >
                  <div class="ds-sidebar-content-header">
                    <div class="ds-sidebar-content-header-wrapper">
                      <DialogTitle
                        class="ds-sidebar-content-header-title"
                        data-testid="ds-sidebar-content-header-title"
                      >
                        <slot name="header">
                          {{ title }}
                        </slot>
                      </DialogTitle>
                      <div class="ds-sidebar-content-header-close">
                        <button
                          class="ds-sidebar-content-header-close-button"
                          data-testid="ds-sidebar-content-header-close-button"
                          @click="onClose"
                        >
                          <DsCloseOutlineIcon />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    class="ds-sidebar-content-body"
                    data-testid="ds-sidebar-content-body"
                  >
                    <slot name="body" />
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import type { PropType } from "vue";

import DsCloseOutlineIcon from "../icon/other/DsCloseOutlineIcon.vue";

import type { DsSidebarProps, DsSideType } from "../../types";

defineOptions({
  name: "DsSidebar",
});

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  side: {
    type: String as PropType<DsSideType>,
    default: "right",
  },
  widthCssClass: {
    type: String,
    default(rawProps: DsSidebarProps) {
      if (rawProps.side === "right") {
        return "ds-sidebar-right-width";
      } else if (rawProps.side === "left") {
        return "ds-sidebar-left-width";
      }
    },
  },
  overflowCssClass: {
    type: String,
    default: "ds-sidebar-content-overflow",
  },
});

const emit = defineEmits(["update:modelValue", "closed", "opened"]);

const isShowing = computed({
  get(): boolean {
    return props.modelValue;
  },
  set(value: boolean) {
    emit("update:modelValue", value);
  },
});

const transition = computed((): string => {
  return props.side === "right"
    ? "ds-sidebar-transition-right"
    : "ds-sidebar-transition-left";
});

function onClose(): void {
  isShowing.value = false;
  emit("closed");
}

watch(isShowing, (newValue) => {
  if (newValue) {
    emit("opened");
  }
});

defineExpose({
  isShowing,
  transition,
});
</script>

<style lang="scss" scoped>
.ds-sidebar {
  @apply fixed inset-0 overflow-hidden;

  &-right-width {
    @apply w-full max-w-3xl;
  }

  &-left-width {
    @apply w-[312px];
  }

  &-transition {
    &-right {
      @apply translate-x-full;
    }

    &-left {
      @apply -translate-x-full;
    }
  }

  &-overlay {
    @apply fixed inset-0 bg-gray-900 bg-opacity-30 transition-opacity;

    &-enter {
      @apply ease-in-out duration-500;
    }
    &-enter-from {
      @apply opacity-0;
    }
    &-enter-to {
      @apply opacity-100;
    }
    &-leave {
      @apply ease-in-out duration-500;
    }
    &-leave-from {
      @apply opacity-100;
    }
    &-leave-to {
      @apply opacity-0;
    }
  }

  &-wrapper {
    @apply absolute inset-0 overflow-hidden;

    &-container {
      @apply pointer-events-none fixed inset-y-0 flex;
    }
  }

  &-right {
    @apply right-0 lg:pl-7;
  }

  &-left {
    @apply left-0 pr-10;
  }

  &-container {
    @apply pointer-events-auto relative w-screen max-w-full;

    &-close {
      @apply absolute top-0 hidden lg:flex pt-5;

      &-button {
        @apply rounded-full bg-gray-900 bg-opacity-50 hover:bg-opacity-100 text-white h-11 w-11 flex items-center justify-center focus:outline-none;
      }

      &-icon {
        @apply h-5 w-5;
      }
    }

    &-right {
      @apply left-0 -ml-16;
    }

    &-left {
      @apply right-0 -mr-16;
    }

    &-enter {
      @apply ease-in-out duration-500;
    }
    &-enter-from {
      @apply opacity-0;
    }
    &-enter-to {
      @apply opacity-100;
    }
    &-leave {
      @apply ease-in-out duration-500;
    }
    &-leave-from {
      @apply opacity-100;
    }
    &-leave-to {
      @apply opacity-0;
    }
  }

  &-content {
    @apply flex h-full flex-col bg-white p-0;

    &-overflow {
      @apply overflow-y-auto;
    }

    &-header {
      @apply border-b border-gray-200 px-5 pt-5 pb-4 lg:hidden;

      &-wrapper {
        @apply flex items-start justify-between;
      }

      &-close {
        @apply ml-3 flex h-7 items-center;

        &-button {
          @apply cursor-pointer text-gray-900 h-5 w-5;
        }
      }

      &-title {
        @apply text-xl font-semibold text-gray-900;
      }
    }

    &-body {
      @apply relative flex-1 pb-24 sm:pb-0 pr-0;
    }
  }

  &-enter {
    @apply transform transition ease-in-out duration-500 sm:duration-700;
  }
  &-enter-to {
    @apply translate-x-0;
  }
  &-leave {
    @apply transform transition ease-in-out duration-500 sm:duration-700;
  }
  &-leave-from {
    @apply translate-x-0;
  }
}
</style>
